import {TableIcon} from '@primer/octicons-react'
import {Fragment} from 'react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {AddedToProjectV2Event$key} from './__generated__/AddedToProjectV2Event.graphql'
import styles from './AddedToProjectV2Event.module.css'
import {ProjectV2} from './ProjectV2'
import {RolledupProjectV2Event} from './RolledupProjectV2Event'
import {TimelineRow} from './row/TimelineRow'

type AddedToProjectV2EventProps = {
  queryRef: AddedToProjectV2Event$key
  issueUrl: string
  rollupGroup?: Record<string, Array<AddedToProjectV2Event$key | AddedToProjectV2Event$key>>
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const AddedToProjectV2EventFragment = graphql`
  fragment AddedToProjectV2Event on AddedToProjectV2Event {
    databaseId
    createdAt
    actor {
      avatarUrl(size: 64)
      login
      profileResourcePath
      __typename
      ... on Bot {
        isCopilot
        isAgent
        displayName
      }
      ... on User {
        name
      }
      ... on EnterpriseUserAccount {
        name
      }
      ... on Mannequin {
        name
      }
    }
    project {
      title
      url
    }
  }
`

export function AddedToProjectV2Event({
  queryRef,
  issueUrl,
  rollupGroup,
  highlightedEventId,
  refAttribute,
}: AddedToProjectV2EventProps): React.ReactElement {
  const {actor, createdAt, project, databaseId} = useFragment(AddedToProjectV2EventFragment, queryRef)

  if (!project) {
    return <></>
  }

  const hightlighted = String(databaseId) === highlightedEventId

  return (
    <TimelineRow
      highlighted={hightlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      leadingIcon={TableIcon}
    >
      <TimelineRow.Main>
        {rollupGroup ? (
          <RolledupProjectV2Event rollupGroup={rollupGroup} />
        ) : (
          <AddedToProjectV2sRendering queryRefs={[queryRef]} />
        )}
      </TimelineRow.Main>
    </TimelineRow>
  )
}

export const AddedToProjectV2sRendering = ({queryRefs}: {queryRefs: AddedToProjectV2Event$key[]}) => {
  if (queryRefs.length === 0) {
    return null
  }

  return (
    <>
      {`${LABELS.timeline.addedThisTo} `}
      {queryRefs.map((queryRef, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <Fragment key={index}>
          <InternalAddedToProjectV2sRendering
            queryRef={queryRef}
            first={index === 0}
            last={index === queryRefs.length - 1}
          />
        </Fragment>
      ))}
    </>
  )
}

const InternalAddedToProjectV2sRendering = ({
  queryRef,
  first,
  last,
}: Pick<AddedToProjectV2EventProps, 'queryRef'> & {
  first: boolean
  last: boolean
}) => {
  const {project} = useFragment(AddedToProjectV2EventFragment, queryRef)

  if (!project?.title || !project?.url) {
    return null
  }

  return (
    <span className={styles.projectNameWrapper}>
      <>
        {!first && !last && <span className={styles.commaSeparator}>,</span>}
        {!first && last && <span className={styles.andSeparator}>{LABELS.timeline.and}</span>}
        <ProjectV2 title={project?.title} url={project?.url} />
      </>
    </span>
  )
}
